import {PagedData, PagedDataItem} from '@app/views/databrowsing/model/paged-data';
import {DistrictEntity, Guid, IEntity, ILightEntity, SystemId} from '@app/views/databrowsing/model/common.data.models';
import {GradeMap} from '@app/core/model/grade.map';
import {LinkedEntities, LinkedEntityItem} from '@app/views/databrowsing/model/linked-entities.models';
import {LineItemModel, ResultModel} from '@app/views/databrowsing/model/gradebook.models';
import {copyData} from '@app/core/model/integration-models';
import {USER_TYPE_PRIORITY_ORDER} from '@app/views/admin/application-settings/models/duplicates.model';
import {Observable} from 'rxjs';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import {DATA_MASKING_ENABLED_METADATA_ATTRIBUTES} from '@app/views/admin/application-settings/models/integration.model';

export class Person extends DistrictEntity {
    otherIds: SystemId[];
    oneRosterHumanReadableIdentifier: string;
    oneRosterUserMasterIdentifier: string;
    name: Name;
    preferredName: string;
    // otherNames: Name[];
    email: Email;
    otherEmails: Email[];
    credentials: Credentials;
    enabledUser: string;
    profilePhotoUrl: string;
    // personType: string;

    address: Address;
    phoneNumber: PhoneNumber;
    otherPhoneNumbers: PhoneNumber[];
    demographic: Demographic;
    medical: Medical;
    insurance: Insurance;
    groupMemberships: GroupMembership[];
    resources: Guid[];

    // Multiple roles
    preferredFirstName: string;
    preferredLastName: string;
    preferredMiddleName: string;
    userRoles: UserRole[];
    userProfiles: UserProfile[];

    piiData: PIIData[];
}

export class DistrictModel extends DistrictEntity implements IEntity {
    stateId: string;
    otherIds: SystemId[];
    schoolNumber: string;
    districtName: string;
    lowGrade: string;
    highGrade: string;
    principal: Name;
    email: Email;

    address: Address;
    phoneNumber: PhoneNumber;
    otherPhoneNumbers: PhoneNumber[];
}

export class DistrictLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    districtName = '';
    createdAt = '';

    constructor(data?: PagedDataItem<DistrictModel>) {
        if (!data) {
            return;
        }
        const district = data.data;
        this.guid = district.guid ? district.guid.guidValue : '';
        this.districtName = district.districtName;
        this.createdAt = district.createdAt;
    }
}

export class SchoolModel extends DistrictEntity implements IEntity {
    stateId: string;
    ncesId: string;
    otherIds: SystemId[];
    schoolNumber: string;
    schoolName: string;
    highGrade: string;
    lowGrade: string;
    principal: Name;
    email: Email;
    // schoolType: string;
    // classification: string;
    // gender: string;
    // boarding: string;

    address: Address;
    phoneNumber: PhoneNumber;
    otherPhoneNumbers: PhoneNumber[];

    primarySchoolId: Guid;
    schoolIds: Guid[];
    schoolIdsAsArray: Guid[];
    type: string;

    studentsTotal: number;
    contactsTotal: number;
    staffTotal: number;
    rostersTotal: number;
    coursesTotal: number;
    calendarsTotal: number;
    groupsTotal: number;
    schoolAdminsTotal: number;
}

export class SchoolLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    schoolName = '';
    schoolNumber = '';
    districtName = '';
    localId = '';
    highGrade = '';
    lowGrade = '';
    ['students.length'] = '';
    ['contacts.length'] = '';
    ['staff.length'] = '';
    ['rosters.length'] = '';
    ['courses.length'] = '';
    ['calendars.length'] = '';
    ['groups.length'] = '';
    ['schoolAdmins.length'] = '';
    createdAt = '';
    lastUpdated = '';

    constructor(data?: PagedDataItem<SchoolModel>) {
        if (!data) {
            return;
        }
        const school = data.data;
        this.guid = school.guid ? school.guid.guidValue : '';
        this.schoolName = school.schoolName;
        this.schoolNumber = school.schoolNumber;
        this.localId = school.localId;
        this.highGrade = GradeMap.has(school.highGrade) ? GradeMap.get(school.highGrade) : school.highGrade;
        this.lowGrade = GradeMap.has(school.lowGrade) ? GradeMap.get(school.lowGrade) : school.lowGrade;
        this['students.length'] = String(school.studentsTotal || 0);
        this['contacts.length'] = String(school.contactsTotal || 0);
        this['staff.length'] = String(school.staffTotal || 0);
        this['rosters.length'] = String(school.rostersTotal || 0);
        this['courses.length'] = String(school.coursesTotal || 0);
        this['calendars.length'] = String(school.calendarsTotal || 0);
        this['groups.length'] = String(school.groupsTotal || 0);
        this['schoolAdmins.length'] = String(school.schoolAdminsTotal || 0);
        this.createdAt = school.createdAt;
        this.lastUpdated = school.lastUpdatedAt;
    }
}

export class StudentModel extends Person implements IEntity {
    stateId: string;
    studentNumber: string;
    grades: string[];

    ellStatus: string;
    lunchStatus: string;
    specEducationStatus: string;
    disabilityOrExceptionality: string;
    secondDisability: string;
    thirdDisability: string;
    placement: string;
    spedExitCode: string;
    transportationFlag: string;

    enrollment: StudentEnrollment;
    otherEnrollments: StudentEnrollment[];
    rosters: string[];
    studentContacts: Guid[];
    emergencyContact: EmergencyContact;

    primarySchoolId: Guid;
    schoolIds: Guid[];
    schoolIdsAsArray: Guid[];
    type: string;

    results: PagedData<ResultModel>;
}

export class StudentLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    oneRosterHumanReadableIdentifier = '';
    studentNumber = '';
    ['name.familyName'] = '';
    ['name.givenName'] = '';
    ['email.emailAddress'] = '';
    ['credentials.username'] = '';
    ['studentEnrollment.schoolId'] = '';
    ['studentEnrollment.schoolName'] = '';
    ['studentEnrollment.grade'] = '';
    // ['studentContacts.length'] = '';
    ['contacts.length'] = '';
    ['rosters.length'] = '';
    createdAt = '';
    lastUpdated = '';
    districtName = '';

    constructor(data?: PagedDataItem<StudentModel>) {
        if (!data) {
            return;
        }
        const student = data.data;
        this.guid = student.guid ? student.guid.guidValue : '';
        this.localId = student.localId;
        this.studentNumber = student.studentNumber;
        this['name.familyName'] = student.name ? student.name.familyName : '';
        this['name.givenName'] = student.name ? student.name.givenName : '';
        this['email.emailAddress'] = student.email ? student.email.emailAddress : '';
        this['credentials.username'] = student.credentials ? student.credentials.username : '';
        this['studentEnrollment.schoolId'] = student.enrollment && student.enrollment.schoolId ? student.enrollment.schoolId.guidValue : '';
        this['studentEnrollment.schoolName'] = data.linkedEntities &&
        data.linkedEntities['enrollment'] && (data.linkedEntities['enrollment'].length > 0) && data.linkedEntities['enrollment'][0].name
            ? data.linkedEntities['enrollment'][0].name
            : this['studentEnrollment.schoolId'];
        if (student.districtId && (this['studentEnrollment.schoolId'] === student.districtId.guidValue)) {
            this.districtName = this['studentEnrollment.schoolName'];
            this['studentEnrollment.schoolName'] = this['studentEnrollment.schoolId'];

            if (!this.districtName || this.districtName === this['studentEnrollment.schoolId']) {
                this.districtName = data.linkedEntities &&
                data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this['studentEnrollment.schoolId'];
            }
        }
        const grade = student.enrollment ? student.enrollment.grade : '';
        this['studentEnrollment.grade'] = (grade && GradeMap.has(grade)) ? GradeMap.get(grade) : grade;
        // this['studentContacts.length'] = student.studentContacts ? String(student.studentContacts.length) : '0';
        this['contacts.length'] = student.studentContacts ? String(student.studentContacts.length) : '0';
        this['rosters.length'] = student.rosters ? String(student.rosters.length) : '0';
        this.createdAt = student.createdAt;
        this.lastUpdated = student.lastUpdatedAt;
        this.oneRosterHumanReadableIdentifier = student.oneRosterHumanReadableIdentifier;
    }
}

export class RosterStudentEnrollmentModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    personId: Guid;
    sectionId: Guid;
    role: EnrollmentRole;
    beginDate: string;
    endDate: string;
    primary: boolean;

    primarySchoolId: Guid;
    schoolIds: Guid[];
    schoolIdsAsArray: Guid[];
    type: string;
}

export class RosterStudentEnrollmentLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    schoolId = '';
    personId = '';
    sectionId = '';
    role = '';
    primary = '';
    createdAt = '';
    lastUpdated = '';
    districtName = '';

    constructor(data?: PagedDataItem<RosterStudentEnrollmentModel>) {
        if (!data) {
            return;
        }
        const enrollment = data.data;
        this.guid = enrollment.guid ? enrollment.guid.guidValue : '';
        this.localId = enrollment.localId;
        this.schoolId = enrollment.schoolId ? enrollment.schoolId.guidValue : '';
        this.sectionId = enrollment.sectionId ? enrollment.sectionId.guidValue : '';
        this.role = enrollment.role;
        this.primary = String(enrollment.primary);
        this.createdAt = enrollment.createdAt;
        this.lastUpdated = enrollment.lastUpdatedAt;
    }
}

export class ContactModel extends Person implements IEntity {
    schoolId: Guid;
    stateId: string;
    employerType: string;
    employerName: string;
    employerPhone: string;

    relationships: ContactRelationship[];
    otherSchoolIds: Guid[];

    primarySchoolId: Guid;
    schoolIds: Guid[];
    schoolIdsAsArray: Guid[];
    type: string;
}

export class ContactLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    oneRosterHumanReadableIdentifier = '';
    ['name.familyName'] = '';
    ['name.givenName'] = '';
    ['name.middleName'] = '';
    ['email.emailAddress'] = '';
    schoolId = '';
    schoolName = '';
    ['students.length'] = '';
    createdAt = '';
    lastUpdated = '';
    districtName = '';

    constructor(data?: PagedDataItem<ContactModel>) {
        if (!data) {
            return;
        }
        const contact = data.data;
        this.guid = contact.guid ? contact.guid.guidValue : '';
        this.localId = contact.localId;
        this['name.familyName'] = contact.name ? contact.name.familyName : '';
        this['name.givenName'] = contact.name ? contact.name.givenName : '';
        this['name.middleName'] = contact.name ? contact.name.middleName : '';
        this['email.emailAddress'] = contact.email ? contact.email.emailAddress : '';
        this.schoolId = contact.schoolId ? contact.schoolId.guidValue : '';
        this.schoolName = data.linkedEntities &&
        data.linkedEntities['school'] && (data.linkedEntities['school'].length > 0) && data.linkedEntities['school'][0].name
            ? data.linkedEntities['school'][0].name
            : this.schoolId;
        if (contact.districtId && (this.schoolId === contact.districtId.guidValue)) {
            this.districtName = this.schoolName;
            this.schoolName = this.schoolId;

            if (!this.districtName || this.districtName === this.schoolId) {
                this.districtName = data.linkedEntities &&
                data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this.schoolId;
            }
        }
        this['students.length'] = contact.relationships ? String(contact.relationships.length) : '0';
        this.createdAt = contact.createdAt;
        this.lastUpdated = contact.lastUpdatedAt;
        this.oneRosterHumanReadableIdentifier = contact.oneRosterHumanReadableIdentifier;
    }
}

export class StaffModel extends Person implements IEntity {
    stateId: string;
    staffNumber: string;

    rosters: string[];
    primaryAssignment: StaffAssignment;
    otherAssignments: StaffAssignment[];

    primarySchoolId: Guid;
    schoolIds: Guid[];
    schoolIdsAsArray: Guid[];
    type: string;
}

export class StaffLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    oneRosterHumanReadableIdentifier = '';
    ['name.familyName'] = '';
    ['name.givenName'] = '';
    ['email.emailAddress'] = '';
    ['credentials.username'] = '';
    ['primaryAssignment.schoolId'] = '';
    ['primaryAssignment.schoolName'] = '';
    // ['students.length'] = '';
    ['rosters.length'] = '';
    createdAt = '';
    lastUpdated = '';
    districtName = '';

    constructor(data?: PagedDataItem<StaffModel>) {
        if (!data) {
            return;
        }
        const staff = data.data;
        this.guid = staff.guid ? staff.guid.guidValue : '';
        this.localId = staff.localId;
        this['name.familyName'] = staff.name ? staff.name.familyName : '';
        this['name.givenName'] = staff.name ? staff.name.givenName : '';
        this['email.emailAddress'] = staff.email ? staff.email.emailAddress : '';
        this['credentials.username'] = staff.credentials ? staff.credentials.username : '';
        this['primaryAssignment.schoolId'] = staff.primaryAssignment && staff.primaryAssignment.schoolId
            ? staff.primaryAssignment.schoolId.guidValue : '';
        this['primaryAssignment.schoolName'] = data.linkedEntities && data.linkedEntities['primaryAssignment'] &&
        (data.linkedEntities['primaryAssignment'].length > 0) && data.linkedEntities['primaryAssignment'][0].name
            ? data.linkedEntities['primaryAssignment'][0].name
            : this['primaryAssignment.schoolId'];
        if (staff.districtId && (this['primaryAssignment.schoolId'] === staff.districtId.guidValue)) {
            this.districtName = this['primaryAssignment.schoolName'];
            this['primaryAssignment.schoolName'] = this['primaryAssignment.schoolId'];

            if (!this.districtName || this.districtName === this['primaryAssignment.schoolId']) {
                this.districtName = data.linkedEntities &&
                data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this['primaryAssignment.schoolId'];
            }
        }
        this['rosters.length'] = staff.rosters ? String(staff.rosters.length) : '0';
        this.createdAt = staff.createdAt;
        this.lastUpdated = staff.lastUpdatedAt;
        this.oneRosterHumanReadableIdentifier = staff.oneRosterHumanReadableIdentifier;
    }
}

export class CourseModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    otherIds: SystemId[];
    courseTitle: string;
    courseSchoolId: string;
    courseDistrictId: string;
    description: string;
    schoolYearId: Guid;
    schoolYear: string;
    subjects: string[];
    grades: string[];
    scedCode: string;
    scedLevel: string;
    scedSubjects: string[];
    duration: string;

    rosters: string[];
    otherSchoolIds: Guid[];

    primarySchoolId: Guid;
    schoolIds: Guid[];
    schoolIdsAsArray: Guid[];
    type: string;
}

export class CourseLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    courseTitle = '';
    description = '';
    schoolId = '';
    schoolName = '';
    schoolYearId = '';
    schoolYearName = '';
    ['rosters.length'] = '';
    createdAt = '';
    lastUpdated = '';
    districtName = '';

    constructor(data?: PagedDataItem<CourseModel>) {
        if (!data) {
            return;
        }
        const course = data.data;
        this.guid = course.guid ? course.guid.guidValue : '';
        this.localId = course.localId;
        this.courseTitle = course.courseTitle;
        this.description = course.description;
        this.schoolId = course.schoolId ? course.schoolId.guidValue : '';
        this.schoolName = data.linkedEntities &&
        data.linkedEntities['school'] && (data.linkedEntities['school'].length > 0) &&
        data.linkedEntities['school'][0] && data.linkedEntities['school'][0].name
            ? data.linkedEntities['school'][0].name
            : this.schoolId;
        if (course.districtId && (this.schoolId === course.districtId.guidValue)) {
            this.districtName = this.schoolName;
            this.schoolName = this.schoolId;

            if (!this.districtName || this.districtName === this.schoolName) {
                this.districtName = data.linkedEntities &&
                data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this.schoolName;
            }
        }
        this.schoolYearId = course.schoolYearId ? course.schoolYearId.guidValue : '';
        this.schoolYearName = data.linkedEntities && data.linkedEntities['schoolYearId'] &&
        (data.linkedEntities['schoolYearId'].length > 0) && data.linkedEntities['schoolYearId'] &&
        data.linkedEntities['schoolYearId'][0] && data.linkedEntities['schoolYearId'][0].name
            ? data.linkedEntities['schoolYearId'][0].name
            : (course?.schoolYear || '');
        this['rosters.length'] = course.rosters ? String(course.rosters.length) : '0';
        this.createdAt = course.createdAt;
        this.lastUpdated = course.lastUpdatedAt;
    }
}

export class RosterModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    sectionId: string;
    sectionNumber: string;
    sectionName: string;
    sectionAlias: string;
    course: Guid;
    courseTitle: string;
    schoolYear: string;
    grades: string[];
    periods: string[];
    classType: string;
    subjectCodes: string[];
    subjects: string[];

    classBeginningTime: string;
    classEndingTime: string;
    roomNumber: string;
    // section: Guid;

    students: PersonReference[];
    calendars: Guid[];
    primaryStaff: TeacherRosterReference;
    otherStaffs: TeacherRosterReference[];

    primarySchoolId: Guid;
    schoolIds: Guid[];
    schoolIdsAsArray: Guid[];
    type: string;

    lineItems: PagedData<LineItemModel>;
}

export class RosterLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    sectionName = '';
    sectionAlias = '';
    sectionNumber = '';
    course = '';
    courseName = '';
    schoolYear = '';
    ['students.length'] = '';
    ['staff.length'] = '';
    ['calendars.length'] = '';
    createdAt = '';
    lastUpdated = '';
    districtName = '';

    constructor(data?: PagedDataItem<RosterModel>) {
        if (!data) {
            return;
        }
        const roster = data.data;
        this.guid = roster.guid ? roster.guid.guidValue : '';
        this.localId = roster.localId;
        this.sectionName = roster.sectionName;
        this.sectionAlias = roster.sectionAlias;
        this.sectionNumber = roster.sectionNumber;
        this.course = roster.course ? roster.course.guidValue : '';
        this.courseName = data.linkedEntities &&
        data.linkedEntities['course'] && (data.linkedEntities['course'].length > 0) && data.linkedEntities['course'][0].name
            ? data.linkedEntities['course'][0].name
            : '';
        this.courseName = this.courseName ? this.courseName : roster.courseTitle || this.course;
        this.schoolYear = roster.schoolYear;
        const staffsLength = (roster.primaryStaff ? 1 : 0) + (roster.otherStaffs ? roster.otherStaffs.length : 0);
        this['students.length'] = roster.students ? String(roster.students.length) : '0';
        this['staff.length'] = String(staffsLength);
        this['calendars.length'] = roster.calendars ? String(roster.calendars.length) : '0';
        this.createdAt = roster.createdAt;
        this.lastUpdated = roster.lastUpdatedAt;
    }
}

export class CalendarModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    title: string;
    schoolYear: string;
    startDate: string;
    endDate: string;
    sessionType: string;
    parent: Guid;

    children: Guid[];
    rosters: string[];
    otherSchoolIds: Guid[];

    primarySchoolId: Guid;
    schoolIds: Guid[];
    schoolIdsAsArray: Guid[];
    type: string;
}

export class CalendarLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    title = '';
    schoolYear = '';
    startDate = '';
    endDate = '';
    schoolId = '';
    schoolName = '';
    districtName = '';

    constructor(data?: PagedDataItem<CalendarModel>) {
        if (!data) {
            return;
        }
        const calendar = data.data;
        this.guid = calendar.guid ? calendar.guid.guidValue : '';
        this.localId = calendar.localId;
        this.title = calendar.title;
        this.schoolYear = calendar.schoolYear;
        this.startDate = calendar.startDate;
        this.endDate = calendar.endDate;
        this.schoolId = calendar.schoolId ? calendar.schoolId.guidValue : '';
        this.schoolName = data.linkedEntities &&
        data.linkedEntities['school'] && (data.linkedEntities['school'].length > 0) && data.linkedEntities['school'][0].name
            ? data.linkedEntities['school'][0].name
            : this.schoolId;
        if (calendar.districtId && (this.schoolId === calendar.districtId.guidValue)) {
            this.districtName = this.schoolName;
            this.schoolName = this.schoolId;

            if (!this.districtName || this.districtName === this.schoolName) {
                this.districtName = data.linkedEntities &&
                data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this.schoolName;
            }
        }
    }
}

export class GroupModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    name: string;
    distinguishedName: string;
    groupType: string;

    members: GroupMember[];
    parentGroups: GroupMembership[];
    childGroups: GroupMembership[];

    primarySchoolId: Guid;
    schoolIds: Guid[];
    schoolIdsAsArray: Guid[];
    type: string;
}

export class GroupLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    name = '';
    distinguishedName = '';
    groupType = '';
    createdAt = '';
    lastUpdated = '';
    districtName = '';

    constructor(data?: PagedDataItem<GroupModel>) {
        if (!data) {
            return;
        }
        const group = data.data;
        this.guid = group.guid ? group.guid.guidValue : '';
        this.name = group.name;
        this.distinguishedName = group.distinguishedName;
        this.groupType = group.groupType;
        this['members.length'] = group.members ? String(group.members.length) : '0';
        this.createdAt = group.createdAt;
        this.lastUpdated = group.lastUpdatedAt;
    }
}

export class SchoolAdminModel extends StaffModel implements IEntity {
}

export class SchoolAdminLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    oneRosterHumanReadableIdentifier = '';
    stateId = '';
    ['name.familyName'] = '';
    ['name.givenName'] = '';
    ['name.middleName'] = '';
    ['email.emailAddress'] = '';
    ['primaryAssignment.schoolId'] = '';
    ['primaryAssignment.schoolName'] = '';
    ['rosters.length'] = '';
    createdAt = '';
    lastUpdated = '';
    districtName = '';

    constructor(data?: PagedDataItem<SchoolAdminModel>) {
        if (!data) {
            return;
        }
        const schoolAdmin = data.data;
        this.guid = schoolAdmin.guid ? schoolAdmin.guid.guidValue : '';
        this.localId = schoolAdmin.localId;
        this.stateId = schoolAdmin.stateId;
        this['name.familyName'] = schoolAdmin.name ? schoolAdmin.name.familyName : '';
        this['name.givenName'] = schoolAdmin.name ? schoolAdmin.name.givenName : '';
        this['name.middleName'] = schoolAdmin.name ? schoolAdmin.name.middleName : '';
        this['email.emailAddress'] = schoolAdmin.email ? schoolAdmin.email.emailAddress : '';
        this['primaryAssignment.schoolId'] = schoolAdmin.primaryAssignment && schoolAdmin.primaryAssignment.schoolId
            ? schoolAdmin.primaryAssignment.schoolId.guidValue : '';
        this['primaryAssignment.schoolName'] = data.linkedEntities && data.linkedEntities['primaryAssignment'] &&
        (data.linkedEntities['primaryAssignment'].length > 0) && data.linkedEntities['primaryAssignment'][0].name
            ? data.linkedEntities['primaryAssignment'][0].name
            : this['primaryAssignment.schoolId'];
        if (schoolAdmin.districtId && (this['primaryAssignment.schoolId'] === schoolAdmin.districtId.guidValue)) {
            this.districtName = this['primaryAssignment.schoolName'];
            this['primaryAssignment.schoolName'] = this['primaryAssignment.schoolId'];

            if (!this.districtName || this.districtName === this['primaryAssignment.schoolName']) {
                this.districtName = data.linkedEntities &&
                data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this['primaryAssignment.schoolName'];
            }
        }
        this['rosters.length'] = schoolAdmin.rosters ? String(schoolAdmin.rosters.length) : '0';
        this.createdAt = schoolAdmin.createdAt;
        this.lastUpdated = schoolAdmin.lastUpdatedAt;
        this.oneRosterHumanReadableIdentifier = schoolAdmin.oneRosterHumanReadableIdentifier;
    }
}

export class Name {
    nameType: string;
    prefix: string;
    familyName: string;
    givenName: string;
    middleName: string;
    suffix: string;
}

export class Credentials {
    username: string;
    password: string;
    type: string;
    properties: CredentialsProperties;
}

export class CredentialsProperties {
    [key: string]: string;
}

export class Email {
    emailAddress: string;
    emailType: string;
}

export class Address {
    addressType: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
}

export class PhoneNumber {
    number: string;
    phoneNumberType: string;
}

export class Language {
    index: string;
    lang: string;
}

export class Demographic {
    gender: string;
    genderDenom: string;
    birthDate: string;
    countryOfBirth: Country;
    stateOfBirth: State;
    cityOfBirth: string;
    languages: Array<string | Language>;
    nationalities: string[];
    usCitizenshipStatus: string;
    ethnicity: string;
    americanIndianOrAlaskaNative: boolean;
    asian: boolean;
    blackOrAfricanAmerican: boolean;
    hispanicLatinoEthnicity: boolean;
    nativeHawaiianOrOtherPacificIslander: boolean;
    white: boolean;
    demographicRaceTwoOrMoreRaces: boolean;
    schoolResidenceStatus: string;
    notEmpty: boolean;
}

export class Country {
    isoCountryAlpha2: string;
}

export class State {
    stateCode: string;
}

export class Medical {
    height: string;
    weight: string;
    bloodPressure: string;
    pulse: string;
    vision: string;
    medication: string;
    allergies: string;
    notes: string;
    conditions: string;
    physicianName: string;
    physicianPhone: string;
    physicianGroup: string;
    alertGuardian: string;
    alertGuardianExpires: string;
    alertMedical: string;
    alertMedicalExpires: string;
    alertOther: string;
    alertOtherExpires: string;
}

export class Insurance {
    name: string;
    number: string;
    phone: string;
}

export class StudentEnrollment {
    schoolId: Guid;
    grade: string;
    gradeDenom: string;
    schoolYear: string;
    homeRoomNumber: string;
    projectedGraduationYear: string;
    membershipType: string;
    responsibleSchoolType: string;
    entryDate: string;
    exitDate: string;
    withdrawalDate: string;
    // homeRoomTeacher: PersonReference;
    // counselor: PersonReference;

    static getLinks(links: LinkedEntityItem[], enrollments: StudentEnrollment[], replaceLink?: LinkedEntityItem): LinkedEntityItem[] {
        let mappedLinks = LinkedEntities.getLinks<StudentEnrollment>(
            links,
            enrollments,
            (enrollment: StudentEnrollment) => <LinkedEntityItem>{guid: Guid.getValue(enrollment.schoolId)},
            (enrollment: StudentEnrollment) => Guid.getValue(enrollment.schoolId)
        );

        if (replaceLink) {
            mappedLinks = mappedLinks.map((mappedLink: LinkedEntityItem) => mappedLink.guid == replaceLink.guid ? replaceLink : mappedLink);
        }

        return mappedLinks;
    }
}

export class PersonReference {
    guid: Guid;
    // localId: string;
    givenName: string;
    familyName: string;

    static getLinks(links: LinkedEntityItem[], persons: PersonReference[]): LinkedEntityItem[] {
        return LinkedEntities.getLinks<PersonReference>(
            links,
            persons,
            (person: PersonReference) => {
                const name = [person.givenName, person.familyName].join(' ');
                return <LinkedEntityItem>{
                    guid: Guid.getValue(person.guid),
                    name: (name === ' ') ? '' : name
                };
            },
            (person: PersonReference) => Guid.getValue(person.guid)
        );
    }
}

export class GroupMember extends PersonReference {
    role: string;
    // deleted: boolean;
}

export class EmergencyContact {
    name: string;
    phone: string;
    relationship: string;
}

export class TeacherRosterReference extends PersonReference {
    // teacherOfRecord: boolean;
    // percentResponsible: string;
    // primary: boolean;
}

export class GroupMembership {
    groupGuid: Guid;
    role: string;
    groupSourceId: string;

    // deleted: boolean;

    static getLinks(links: LinkedEntityItem[], groupMemberships: GroupMembership[]): LinkedEntityItem[] {
        return LinkedEntities.getLinks<GroupMembership>(
            links,
            groupMemberships,
            (group: GroupMembership) => <LinkedEntityItem>{guid: Guid.getValue(group.groupGuid), name: group.groupSourceId},
            (group: GroupMembership) => Guid.getValue(group.groupGuid)
        );
    }
}

export class ContactRelationship {
    studentId: Guid;
    type: string;
    relationship: string;
    // restrictions: string;
    // livesWith: boolean;
    // primaryContact: boolean;
    // emergencyContact: boolean;
    // financialResponsibility: boolean;
    // custodial: boolean;
    // communication: boolean;
    // contactSequence: number;

    static getLinks(links: LinkedEntityItem[], relationships: ContactRelationship[]): LinkedEntityItem[] {
        return LinkedEntities.getLinks<ContactRelationship>(
            links,
            relationships,
            (relationship: ContactRelationship) => <LinkedEntityItem>{guid: Guid.getValue(relationship.studentId)},
            (relationship: ContactRelationship) => Guid.getValue(relationship.studentId)
        );
    }
}

export class StaffAssignment {
    schoolId: Guid;

    // staffJobFunction: string;

    static getLinks(links: LinkedEntityItem[], assignments: StaffAssignment[], replaceLink?: LinkedEntityItem): LinkedEntityItem[] {
        let mappedLinks = LinkedEntities.getLinks<StaffAssignment>(
            links,
            assignments,
            (assignment: StaffAssignment) => <LinkedEntityItem>{guid: Guid.getValue(assignment.schoolId)},
            (assignment: StaffAssignment) => Guid.getValue(assignment.schoolId)
        );

        if (replaceLink) {
            mappedLinks = mappedLinks.map((mappedLink: LinkedEntityItem) => mappedLink.guid == replaceLink.guid ? replaceLink : mappedLink);
        }

        return mappedLinks;
    }
}

export class UserRole {
    isPrimaryOrgForPersonType: boolean; // допоміжне поле (чи є ця роль primary в контексті самого person'а)
    roleTypeForThisOrg: RoleType;       // PRIMARY, SECONDARY в контексті кожної з шкіл
    role: string;                       // сама роль
    orgGuid: Guid;
    userProfile: string;
    beginDate: string;
    endDate: string;
    personType: PersonType;             // допоміжне поле - STUDENT / TEACHER / ...
    orgName: string;

    static equalOrgRole(a: UserRole, b: UserRole): boolean {
        return Guid.getValue(a.orgGuid) === Guid.getValue(b.orgGuid)
            && a.role === b.role;
    }

    static hasDataToMerge(a: UserRole, b: UserRole): boolean {
        return UserRole.equalOrgRole(a, b)
            && (a.roleTypeForThisOrg != b.roleTypeForThisOrg
                || a.userProfile != b.userProfile
                || a.beginDate != b.beginDate
                || a.endDate != b.endDate
            );
    }

    static mergeList(userRolesA: UserRole[], userRolesB: UserRole[]): UserRole[] {
        const result = copyData(userRolesA);
        userRolesB.forEach((userRoleB: UserRole) => {
            const index = UserRole.findIndex(result, userRoleB);
            if (index > -1) {
                UserRole.merge(result[index], userRoleB);
            } else {
                result.push(userRoleB);
            }
        });
        return result;
    }

    static merge(a: UserRole, b: UserRole): UserRole {
        a.roleTypeForThisOrg = b.roleTypeForThisOrg;
        a.userProfile = b.userProfile;
        a.beginDate = b.beginDate;
        a.endDate = b.endDate;
        return a;
    }

    static isListDifferent(array1: UserRole[], array2: UserRole[]) {
        return (array2 || []).some((item2: UserRole) =>
            array1.some((item1: UserRole) => UserRole.hasDataToMerge(item1, item2))
            || (UserRole.findIndex(array1, item2) === -1)
        );
    }

    static findIndex(userRoles: UserRole[], userRole: UserRole) {
        return userRoles.findIndex((item: UserRole) => UserRole.equalOrgRole(item, userRole));
    }

    static toString(userRole: UserRole): string {
        // return `${userRole.roleTypeForThisOrg === RoleType.PRIMARY.toUpperCase() ? 'Primary' : ''} Role ${userRole.role} (${userRole.personType}) of \
        return `${userRole.roleTypeForThisOrg === RoleType.PRIMARY.toUpperCase() ? 'Primary' : ''} Role ${userRole.role} of \
${userRole.isPrimaryOrgForPersonType ? 'Primary' : ''} ${userRole.orgName || Guid.getValue(userRole.orgGuid)}\
${userRole.userProfile ? userRole.userProfile + ' ' : ''}\
${(userRole.beginDate || userRole.endDate) ? (userRole.beginDate || 'n/a') + ' - ' + (userRole.endDate || 'n/a') : ''}`;
    }

    static sort(userRoles: UserRole[], sortByIsPrimaryOrgForPersonType = true): UserRole[] {
        userRoles.sort((a: UserRole, b: UserRole) => {
            if (sortByIsPrimaryOrgForPersonType && a.isPrimaryOrgForPersonType) {
                return -1;
            }
            if (sortByIsPrimaryOrgForPersonType && b.isPrimaryOrgForPersonType) {
                return 1;
            }
            const diff = (USER_TYPE_PRIORITY_ORDER[a.personType] || 5) - (USER_TYPE_PRIORITY_ORDER[b.personType] || 5);
            return diff || a.role.toLowerCase().localeCompare(b.role.toLowerCase());
        });
        return userRoles;
    }
}

export const PII_METADATA_KEY_PREFIX = 'pii';

// As is in DATA_MASKING_ENABLED_ATTRIBUTES
export class PIIData {
    clientGid: string;
    givenName: string;
    familyName: string;
    middleName: string;
    emailAddress: string;
    username: string;
    birthDate: string;
    number: string;
    person: Person;
    application$: Observable<Application>;

    constructor(clientGid: string) {
        this.clientGid = clientGid;
    }

    static getPIIDataList(data: PagedDataItem<DistrictEntity>): PIIData[] {
        const result: PIIData[] = [];
        if (data.data.metadata) {
            Object.entries(data.data.metadata).forEach(([key, value]: [string, string]) => {
                const arr1 = key.split(':');
                if ((arr1.length > 1) && (arr1[0] === PII_METADATA_KEY_PREFIX)) {
                    const clientGidAndPropertyName = arr1[1];
                    const arr2 = clientGidAndPropertyName.split('.');
                    if (arr2.length > 1) {
                        const propertyName = arr2[arr2.length - 1];
                        if (DATA_MASKING_ENABLED_METADATA_ATTRIBUTES.indexOf(propertyName) > -1) {
                            // Is PII data
                            const clientGid = arr2[arr2.length - 2];
                            let clientPIIData = result.find((item: PIIData) => item.clientGid === clientGid);

                            if (!clientPIIData) {
                                clientPIIData = new PIIData(clientGid);
                                result.push(clientPIIData);
                            }

                            clientPIIData[propertyName] = value;
                            delete data.data.metadata[key];
                        }
                    }
                }
            });
        }
        result.forEach((item: PIIData) =>
            item.createPerson()
        );
        return (result.length > 0) ? result : undefined;
    }

    createPerson() {
        this.person = new Person();
        this.person.name = <Name>{
            givenName: this.givenName,
            familyName: this.familyName,
            middleName: this.middleName
        };
        this.person.email = <Email>{emailAddress: this.emailAddress};
        this.person.credentials = <Credentials>{username: this.username};
        this.person.demographic = <Demographic>{birthDate: this.birthDate};
        this.person.phoneNumber = <PhoneNumber>{number: this.number};
    }
}

export class UserProfile {
    profileId: string;
    profileType: string;
    vendorId: string;
    applicationId: string;
    description: string;
    credentials: Credentials;
}

export enum EntityStatus {
    ACTIVE = 'ACTIVE',
    APPLIED = 'APPLIED',
    ADMITTED = 'ADMITTED',
    ENROLLED = 'ENROLLED',
    PENDING = 'PENDING',
    WAITLISTED = 'WAITLISTED',
    INACTIVE = 'INACTIVE',
    GRADUATED = 'GRADUATED',
    TO_BE_DELETED = 'TO_BE_DELETED',
    DECLINED = 'DECLINED',
    WITHDRAWN = 'WITHDRAWN'
}

export enum EnrollmentRole {
    STUDENT = 'STUDENT',
    TEACHER = 'TEACHER',
    PARENT = 'PARENT',
    GUARDIAN = 'GUARDIAN',
    RELATIVE = 'RELATIVE',
    AIDE = 'AIDE',
    ADMINISTRATOR = 'ADMINISTRATOR',
    PROCTOR = 'PROCTOR'
}

export enum RoleType {
    PRIMARY = 'Primary', // 'PRIMARY'
    SECONDARY = 'Secondary' // 'SECONDARY'
}

export enum PersonType {
    TEACHER = 'Teacher',
    STUDENT = 'Student',
    GUARDIAN = 'Guardian',
    SCHOOL_ADMIN = 'School Admin', // 'SchoolAdmin'
    CONTACT = 'Contact'
}
